
import React, { useState, useEffect, useRef } from 'react';
import { ViewType } from '../types';
import { IconSearch, IconSparkles, IconLightning, IconCommand, IconClose } from './Icons';
import { chatWithAiQ } from '../services/geminiService';
import toast from 'react-hot-toast';

interface Command {
  id: string;
  label: string;
  type: 'nav' | 'action' | 'ai';
  view?: ViewType;
  action?: () => void;
  shortcut?: string;
  desc?: string;
}

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (view: ViewType) => void;
}

const CommandPalette: React.FC<CommandPaletteProps> = ({ isOpen, onClose, onNavigate }) => {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Command List
  const commands: Command[] = [
    // Navigation
    { id: 'nav-dash', label: 'Go to Dashboard', type: 'nav', view: ViewType.DASHBOARD, desc: 'Overview and P&L' },
    { id: 'nav-risk', label: 'Go to Risk Management', type: 'nav', view: ViewType.RISK_MANAGEMENT, desc: 'VaR, Stress Tests, Limits' },
    { id: 'nav-quant', label: 'Go to Quant Pricing', type: 'nav', view: ViewType.QUANT_COMPUTING, desc: 'Options Pricing, Greeks' },
    { id: 'nav-ops', label: 'Go to Operations', type: 'nav', view: ViewType.MULTI_ASSET_OPS, desc: 'Trade Blotter & Lifecycle' },
    { id: 'nav-strat', label: 'Go to Strategy Lab', type: 'nav', view: ViewType.STRATEGY_LAB, desc: 'Portfolio Optimization' },
    { id: 'nav-news', label: 'Go to Horizon Scanner', type: 'nav', view: ViewType.NEWS_SCANNER, desc: 'News & Events' },
    { id: 'nav-ipv', label: 'Go to IPV Control', type: 'nav', view: ViewType.IPV_CONTROL, desc: 'Valuation Control' },
    { id: 'nav-mkt', label: 'Go to Market Data', type: 'nav', view: ViewType.MARKET_DATA, desc: 'Curves & Surfaces' },
    
    // Actions (Simulated)
    { id: 'act-stress', label: 'Run Global Stress Test', type: 'action', action: () => { toast.loading('Running Global Stress Test...', { duration: 2000 }); setTimeout(() => toast.success('Stress Test Complete: -2.4% Impact'), 2000); }, shortcut: '⇧S', desc: 'Simulate 2008 Crisis Scenario' },
    { id: 'act-report', label: 'Generate Risk Report', type: 'action', action: () => toast.success('PDF Report sent to email'), desc: 'Daily Risk Summary' },
    { id: 'act-cache', label: 'Clear Cache', type: 'action', action: () => window.location.reload(), desc: 'Reload Application' },
  ];

  const filteredCommands = commands.filter(cmd => 
    cmd.label.toLowerCase().includes(query.toLowerCase()) || 
    cmd.desc?.toLowerCase().includes(query.toLowerCase())
  );

  // Keyboard Navigation inside the list
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Focus Input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
      setQuery('');
      setSelectedIndex(0);
    }
  }, [isOpen]);

  const executeCommand = async (cmd: Command) => {
    if (cmd.type === 'nav' && cmd.view) {
      onNavigate(cmd.view);
      toast(`Navigated to ${cmd.label.replace('Go to ', '')}`, { icon: '🚀' });
    } else if (cmd.type === 'action' && cmd.action) {
      cmd.action();
    }
    onClose();
  };

  const handleEnter = async () => {
    // Check if it's an AI Query (starts with ?)
    if (query.startsWith('?')) {
        const question = query.substring(1).trim();
        if (!question) return;

        setIsProcessing(true);
        const result = await chatWithAiQ(question, 'Command Palette');
        setIsProcessing(false);
        onClose();
        
        // Show result in a large toast
        toast((t) => (
            <div onClick={() => toast.dismiss(t.id)}>
                <div className="font-bold text-sm mb-1 text-blue-400">AiQ Answer:</div>
                <div className="text-sm">{result}</div>
            </div>
        ), { duration: 6000, icon: '🤖', style: { maxWidth: '500px' } });
        
        return;
    }

    if (filteredCommands.length > 0) {
      executeCommand(filteredCommands[selectedIndex]);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh]">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity" 
        onClick={onClose}
      ></div>

      {/* Palette */}
      <div className="relative w-full max-w-xl bg-white rounded-xl shadow-2xl overflow-hidden animate-scale-up border border-slate-700">
        
        {/* Search Input */}
        <div className="flex items-center px-4 py-4 border-b border-slate-100">
          <IconSearch />
          <input
            ref={inputRef}
            type="text"
            className="flex-1 ml-3 bg-transparent text-lg outline-none text-slate-800 placeholder-slate-400 font-medium"
            placeholder="Type a command or '?' to ask AI..."
            value={query}
            onChange={(e) => { setQuery(e.target.value); setSelectedIndex(0); }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setSelectedIndex(i => Math.min(i + 1, filteredCommands.length - 1));
              }
              if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setSelectedIndex(i => Math.max(i - 1, 0));
              }
              if (e.key === 'Enter') handleEnter();
            }}
          />
          <div className="flex gap-2 items-center">
             <button 
               onClick={onClose}
               className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600"
             >
               <IconClose />
             </button>
          </div>
        </div>

        {/* Results List */}
        <div className="max-h-[300px] overflow-y-auto custom-scrollbar bg-slate-50 p-2">
          {query.startsWith('?') ? (
             <div className="p-4 flex items-center gap-4 text-blue-600">
                <div className={`p-2 rounded-full bg-blue-100 ${isProcessing ? 'animate-pulse' : ''}`}>
                    <IconSparkles />
                </div>
                <div>
                    <div className="font-bold text-sm">Ask AiQ Intelligence</div>
                    <div className="text-xs text-slate-500">{isProcessing ? 'Thinking...' : `Press Enter to ask: "${query.substring(1)}"`}</div>
                </div>
             </div>
          ) : filteredCommands.length > 0 ? (
            filteredCommands.map((cmd, index) => (
              <div
                key={cmd.id}
                onClick={() => executeCommand(cmd)}
                className={`flex items-center justify-between px-4 py-3 rounded-lg cursor-pointer transition-colors ${
                  index === selectedIndex ? 'bg-blue-600 text-white' : 'hover:bg-slate-200 text-slate-700'
                }`}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                <div className="flex items-center gap-3">
                  {cmd.type === 'nav' ? <IconCommand /> : <IconLightning />}
                  <div>
                      <div className={`text-sm font-medium ${index === selectedIndex ? 'text-white' : 'text-slate-800'}`}>{cmd.label}</div>
                      <div className={`text-xs ${index === selectedIndex ? 'text-blue-200' : 'text-slate-500'}`}>{cmd.desc}</div>
                  </div>
                </div>
                {cmd.shortcut && (
                    <span className={`text-xs font-mono px-2 py-1 rounded ${
                        index === selectedIndex ? 'bg-blue-500 text-blue-100' : 'bg-slate-200 text-slate-500'
                    }`}>
                        {cmd.shortcut}
                    </span>
                )}
              </div>
            ))
          ) : (
            <div className="p-8 text-center text-slate-400 text-sm">
              No commands found for "{query}"
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 bg-slate-100 border-t border-slate-200 text-[10px] text-slate-500 flex justify-between">
           <span>Pro Tip: Start with <span className="font-bold text-slate-700">?</span> to ask Gemini</span>
           <span>AiQ Command Center</span>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
